import { auditDate } from "../../utils/auditStamp";
import { listAcomptes, createAcompte } from "../../services/acompteApi";
import PageHeading from "../../components/PageHeading";
import SearchPending from "../../components/SearchPending";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { createListSearch } from "../../utils/listSearch";
import { apiErrorMessage } from "../../utils/apiError";
import { useEffect, useState } from "react";
import { Modal, message } from "antd";
import { Plus } from "lucide-react";
import { candidateName } from "../../utils/candidateName";
import axios from "../../axios/AxiosInstance";

const statuses = {
  "En cours de traitement": "bg-blue-50 text-blue-800",
  "En attente RH": "bg-amber-50 text-amber-800",
  Validé: "bg-emerald-50 text-emerald-800",
  Refusé: "bg-rose-50 text-rose-800",
};
const statusLabel = (value) =>
  ({
    "En cours": "En cours de traitement",
    Validée: "Validé",
    Refusée: "Refusé",
    pending: "En cours de traitement",
    processing: "En cours de traitement",
    "À transmettre": "En attente RH",
    awaiting_hr: "En attente RH",
    approved: "Validé",
    validated: "Validé",
    Validé: "Validé",
    rejected: "Refusé",
    refused: "Refusé",
    Refusé: "Refusé",
  })[value] ||
  value ||
  "En cours de traitement";
const money = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value))
    ? new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value))
    : "—";

export default function AcomptesPage({
  candidateId,
  candidate,
  embedded = false,
}) {
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [form, setForm] = useState({
    candidateId: "",
    amount: "",
    comment: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const refresh = () =>
      listAcomptes(controller.signal)
        .then((data) => {
          if (!active) return;
          setRows(data);
          setLoadError("");
        })
        .catch((error) => {
          const detail = apiErrorMessage(error, "Impossible de charger les acomptes.");
          if (active && detail) setLoadError(detail);
        });
    refresh();
    window.addEventListener("focus", refresh);
    return () => {
      active = false;
      controller.abort();
      window.removeEventListener("focus", refresh);
    };
  }, []);
  const openRequest = () => {
    setForm({
      candidateId: candidateId || "",
      amount: "",
      comment: "",
    });
    setOpen(true);
    if (!candidate) {
      axios
        .get("tempworker-candidate/freelance")
        .then(({ data }) => setCandidates(Array.isArray(data) ? data : []))
        .catch((error) => message.error(apiErrorMessage(error, "Impossible de charger les intérimaires.")));
    }
  };
  const saveRequest = async (event) => {
    event.preventDefault();
    if (saving) return;
    const person =
      candidate || candidates.find((row) => row._id === form.candidateId);
    const amount = Number(form.amount);
    if (!person || !Number.isFinite(amount) || amount <= 0) {
      message.error("Vérifiez l’intérimaire et le montant.");
      return;
    }
    setSaving(true);
    try {
      const row = await createAcompte({
        candidateId: person._id,
        amount,
        reason: form.comment,
      });
      setRows((current) => [row, ...current]);
      setOpen(false);
      message.success("Demande transmise à l’agence.");
    } catch (error) {
      message.error(
        apiErrorMessage(error, "Impossible d’enregistrer la demande."),
      );
    } finally {
      setSaving(false);
    }
  };
  const [search, setSearch] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(search);
  const [status, setStatus] = useState("");
  // Ne pas déduire un versement du montant demandé ni un refus du motif de demande.
  const normalized = rows
    .filter(
      (row) =>
        !candidateId ||
        String(row.candidateId || row.candidate?._id || row.candidate || "") ===
          String(candidateId),
    )
    .map((row) => ({
      ...row,
      status: statusLabel(row.status),
      origin: row.source || row.origin || "",
      requestedAmount: row.requestedAmount ?? row.amount,
      paidAmount: row.paidAmount ?? row.amountPaid,
      refusalReason: row.refusalReason || row.rejectionReason || (statusLabel(row.status) === "Refusé" ? row.reason || "" : ""),
      requestDate: row.requestedAt || row.createdAt,
    }));
  const visible = normalized
    .filter(
      createListSearch(appliedSearch, [
        ...(!candidateId ? ["name"] : []),
        "status",
        "origin",
        "requestedAmount",
        "paidAmount",
        "refusalReason",
        "requestDate",
      ]),
    )
    .filter((row) => !status || row.status === status);
  return (
    <div className={embedded ? "text-sm" : ""}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {embedded ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Acomptes</h2>
            <p className="mt-1 text-sm text-slate-500">
              Suivez les demandes d’acompte de cet intérimaire.
            </p>
          </div>
        ) : (
          <PageHeading
            icon="advances"
            title="Acomptes"
            description="Suivez les demandes d’acompte de vos intérimaires."
          />
        )}
        <button
          type="button"
          onClick={openRequest}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white"
        >
          <Plus size={16} /> Demander un acompte
        </button>
      </div>
      <div className="my-5 flex flex-wrap gap-3">
        <input
          aria-label="Rechercher dans les acomptes"
          placeholder={
            candidateId
              ? "Provenance, montant, motif…"
              : "Candidat, provenance, montant, motif…"
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md rounded-lg border border-slate-200 px-3 py-2"
        />
        <SearchPending pending={searching} />
        <select
          aria-label="Filtrer par statut"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2"
        >
          <option value="">Tous les statuts</option>
          {Object.keys(statuses).map((label) => (
            <option key={label}>{label}</option>
          ))}
        </select>
      </div>
      {loadError && (
        <p role="alert" className="mb-4 text-red-700">
          {loadError}
        </p>
      )}
      <div className="app-table-container">
        <table className="app-table" aria-label="Suivi des demandes d’acompte">
          <thead>
            <tr>
              {[
                "Date de la demande",
                "Intérimaire",
                "Provenance",
                "Montant demandé",
                "Montant versé",
                "Statut",
                "Motif du refus",
              ].map((label) => (
                <th key={label}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id || row._id}>
                <td>
                  {auditDate(row.requestDate)}
                  <p className="mt-1 text-xs text-slate-500">
                    {row.author || "Auteur non renseigné"}
                  </p>
                </td>
                <td>
                  <strong>{row.name || "—"}</strong>
                  {row.reason && row.status !== "Refusé" && (
                    <p className="mt-1 text-xs text-slate-500">{row.reason}</p>
                  )}
                </td>
                <td>
                  {{
                    site: "Site",
                    website: "Site",
                    candidate_profile: "Fiche intérimaire",
                    freelance: "Espace freelance",
                    recurrent: "Acompte récurrent",
                    "fiche-interimaire": "Fiche intérimaire",
                  }[row.origin] ||
                    row.origin ||
                    "—"}
                </td>
                <td>{money(row.requestedAmount)}</td>
                <td>{money(row.paidAmount)}</td>
                <td>
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statuses[row.status] || "bg-slate-100 text-slate-700"}`}
                  >
                    {row.status}
                  </span>
                </td>
                <td>
                  {row.status === "Refusé"
                    ? row.refusalReason || "Motif à préciser"
                    : "—"}
                </td>
              </tr>
            ))}
            {!visible.length && (
              <tr>
                <td colSpan={7} className="app-table-message">
                  Aucune demande d’acompte pour ces critères.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Modal
        open={open}
        centered
        title="Demander un acompte à l’agence"
        footer={null}
        onCancel={() => !saving && setOpen(false)}
        closable={!saving}
        maskClosable={!saving}
      >
        {open && (
          <form onSubmit={saveRequest} className="space-y-4 pt-3">
            <p className="text-sm text-slate-500">
              L’agence traite la demande et renseigne son état. Le freelance
              consulte le suivi sans le modifier.
            </p>
            {candidate ? (
              <p className="font-semibold">{candidateName(candidate)}</p>
            ) : (
              <label className="block text-sm">
                Intérimaire
                <select
                  required
                  value={form.candidateId}
                  onChange={(e) =>
                    setForm({ ...form, candidateId: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border p-2"
                >
                  <option value="">Sélectionner un intérimaire</option>
                  {candidates.map((person) => (
                    <option key={person._id} value={person._id}>
                      {candidateName(person)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="block text-sm">
              Montant demandé (€)
              <input
                aria-label="Montant demandé (€)"
                type="number"
                required
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="mt-1 w-full rounded-lg border p-2"
              />
            </label>
            <label className="block text-sm">
              Commentaire
              <textarea
                rows={2}
                maxLength={1000}
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                className="mt-1 w-full rounded-lg border p-2"
              />
            </label>
            <div className="flex justify-end gap-3 border-t pt-4">
              <button
                type="button"
                disabled={saving}
                onClick={() => setOpen(false)}
                className="rounded-lg border px-3 py-2"
              >
                Annuler
              </button>
              <button
                disabled={saving}
                className="rounded-lg bg-blue-700 px-3 py-2 text-white"
              >
                {saving ? "Enregistrement…" : "Enregistrer la demande"}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
